import { useAuth0 } from '@auth0/auth0-react';
import { tasksApis } from '@gleamer/apis';
import { useSystem } from '@ohif/core';
import { useQuery } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import React, { useState } from 'react';
import { useUrlQuery } from '../../hooks/url-query';
import { PageTitle } from '../atoms/PageTitle';
import { PageWrapper } from '../atoms/PageWrapper';
import { ErrorAlert } from '../molecules/ErrorAlert';
import { FinishedTaskAlert } from '../molecules/FinishedTaskAlert';
import { TasksTable } from '../organisms/tasks/TasksTable';

const { fetchMyTasks } = tasksApis;

export function MyTasks() {
  const { getAccessTokenSilently } = useAuth0();
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'createdAt',
      desc: true,
    },
  ]);

  // Core singleton (single ui-next provider owns it) rather than the @ohif/ui
  // `useUserAuthentication()` api — see the note in useSaveLabelPass.
  const { servicesManager } = useSystem();
  const { userAuthenticationService: UserAuthenticationService } =
    servicesManager.services;

  function fetchTasks(currentPage = 0, sorting: SortingState) {
    return async () => {
      // Best-effort token refresh. The request's auth header comes from
      // UserAuthenticationService, so a failed OR hung silent-auth (e.g. no live
      // Auth0 session in e2e/smoke, where auth0-spa-js's lock can deadlock in
      // headless Chrome) must not block the task-list fetch. Race it against a
      // short timeout and proceed regardless.
      try {
        await Promise.race([
          getAccessTokenSilently(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('token refresh timeout')), 1500)
          ),
        ]);
      } catch {
        // proceed with the current token
      }
      return await fetchMyTasks(
        UserAuthenticationService,
        currentPage,
        sorting
      );
    };
  }

  const labelTasksQuery = useQuery({
    queryKey: ['tasks', pageIndex, sorting],
    queryFn: fetchTasks(pageIndex, sorting),
    refetchOnWindowFocus: true,
    keepPreviousData: true,
  });

  const query = useUrlQuery();
  const finishedTaskId = query.has('finished')
    ? parseInt(query.get('finished'), 10)
    : null;
  const finishedTask = finishedTaskId
    ? labelTasksQuery.data?.content?.find(
        task => task.taskId === finishedTaskId
      )
    : null;

  const hasError = query.has('error');

  return (
    <PageWrapper className="w-full">
      <PageTitle title="My tasks" />
      {finishedTask && <FinishedTaskAlert task={finishedTask} />}
      {hasError && <ErrorAlert />}
      <TasksTable
        tasksQuery={labelTasksQuery}
        onPaginationChange={setPagination}
        pagination={{ pageIndex, pageSize }}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </PageWrapper>
  );
}

export default MyTasks;
